import {
  ArrowDownNarrowWide,
  ArrowUpNarrowWide,
  ChevronsUpDown,
  FoldVertical,
  Scan,
  Settings2,
  UnfoldVertical,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { capitalize } from "@/common/utils";
import { getPlural } from "@/common/intervalTypes";
import { HGInterval, HGIntervalType } from "@/common/types";
import {
  autoGroupBy,
  groupByOptions,
  thenByOptions,
  resolveLevelTypes,
} from "@/common/recordTree";
import { RecordsViewOptions } from "@/state/recordsView";
import { useElementWidth } from "@/common/hooks";
import {
  formatFittedRange,
  isFittedToAllRecords,
  useAllRecordsTarget,
} from "@/common/allRecordsRange";
import getGlobalStacks from "@/state/globalStacks";

interface RecordsToolbarProps {
  options: RecordsViewOptions;
  setOption: <K extends keyof RecordsViewOptions>(
    key: K,
    value: RecordsViewOptions[K],
  ) => void;
  interval: HGInterval;
  endInterval: HGInterval | null;
  /**
   * Whether notes are actually being shown. Owned by Records so the toolbar and
   * the body can never disagree: `options.mode` alone is not enough, since a
   * stored "notes" mode falls back to the timeline outside a collection.
   */
  notesMode: boolean;
  /** The scope switch only means anything inside a collection. */
  inCollection: boolean;
  onCollapseAll: () => void;
  onExpandAll: () => void;
}

// "children" is still a valid scope everywhere else — it is only kept out of
// the toolbar for now, so the switch offers the two ends of the range.
const SCOPE_CHOICES = ["direct", "all"] as const;

const SCOPE_LABELS = {
  direct: "Direct",
  children: "Children",
  all: "All",
} as const;

const SCOPE_TITLES = {
  direct: "Only this collection's own records",
  children: "This collection and its immediate subcollections",
  all: "Every nested collection, at any depth",
} as const;

const triggerClass =
  "h-7 px-2 gap-1 font-normal text-[12.5px] text-ink2 hover:bg-row-hover data-[state=open]:bg-row-hover";

/** Shared with `RecordsBand` so the two rows of controls cannot drift apart. */
export const iconButtonClass =
  "w-[30px] h-[30px] rounded-[6px] text-muted-foreground hover:bg-row-hover hover:text-foreground";

function typeLabel(it: HGIntervalType) {
  return capitalize(getPlural(it));
}

export default function RecordsToolbar({
  options,
  setOption,
  interval,
  endInterval,
  notesMode,
  inCollection,
  onCollapseAll,
  onExpandAll,
}: RecordsToolbarProps) {
  const [rootRef, width] = useElementWidth<HTMLDivElement>();
  // Fold the grouping/order controls into the display menu once the toolbar
  // itself — not the viewport — gets too narrow to hold them inline. The
  // threshold is low because the mode switch and search live in the band above
  // now, which left this row some 190px richer.
  const inlineControls = width >= 520;
  // The range covering every record in scope — the collection and its
  // descendants, or the whole space outside one. Null while it is still out, or
  // when nothing in scope is on the time axis at all.
  const { target: allRecordsTarget } = useAllRecordsTarget();
  const fitted = isFittedToAllRecords(allRecordsTarget, interval, endInterval);

  const groupOptions = groupByOptions(interval, endInterval);
  const resolved = resolveLevelTypes(interval, endInterval, options);
  const autoGroup = autoGroupBy(interval, endInterval);
  const thenOptions = resolved[0] ? thenByOptions(resolved[0]) : [];

  const groupLabel = resolved[0] ? typeLabel(resolved[0]) : "—";
  const thenLabel =
    options.thenBy === "none"
      ? "None"
      : resolved[1]
        ? typeLabel(resolved[1])
        : "—";

  const autoGroupLabel = autoGroup ? `Auto (${getPlural(autoGroup)})` : "Auto";

  const groupByMenu = (
    <DropdownMenuRadioGroup
      value={options.groupBy}
      onValueChange={(value) =>
        setOption("groupBy", value as RecordsViewOptions["groupBy"])
      }
    >
      <DropdownMenuRadioItem value="auto" className="text-[13px]">
        {autoGroupLabel}
      </DropdownMenuRadioItem>
      {groupOptions.map((it) => (
        <DropdownMenuRadioItem key={it} value={it} className="text-[13px]">
          {typeLabel(it)}
        </DropdownMenuRadioItem>
      ))}
    </DropdownMenuRadioGroup>
  );

  const thenByMenu = (
    <DropdownMenuRadioGroup
      value={options.thenBy}
      onValueChange={(value) =>
        setOption("thenBy", value as RecordsViewOptions["thenBy"])
      }
    >
      <DropdownMenuRadioItem value="auto" className="text-[13px]">
        Auto
      </DropdownMenuRadioItem>
      <DropdownMenuRadioItem value="none" className="text-[13px]">
        None
      </DropdownMenuRadioItem>
      {thenOptions.map((it) => (
        <DropdownMenuRadioItem key={it} value={it} className="text-[13px]">
          {typeLabel(it)}
        </DropdownMenuRadioItem>
      ))}
    </DropdownMenuRadioGroup>
  );

  const toggleOrder = () =>
    setOption("order", options.order === "asc" ? "desc" : "asc");

  const OrderIcon =
    options.order === "asc" ? ArrowDownNarrowWide : ArrowUpNarrowWide;

  return (
    <div
      ref={rootRef}
      id="records-toolbar"
      className="flex h-[44px] min-w-0 shrink-0 items-center gap-2 overflow-x-auto border-b border-border bg-background px-3 [&::-webkit-scrollbar]:hidden"
    >
      {!notesMode && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={iconButtonClass}
              title="Display options"
            >
              <Settings2 className="size-[15px]" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="min-w-56">
            {!inlineControls && (
              <>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger className="text-[13px]">
                    Group by · {groupLabel}
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>{groupByMenu}</DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger className="text-[13px]">
                    Then by · {thenLabel}
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>{thenByMenu}</DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuSeparator />
              </>
            )}

            <DropdownMenuCheckboxItem
              className="text-[13px]"
              checked={options.showGaps}
              onCheckedChange={(checked) => setOption("showGaps", !!checked)}
            >
              Show gaps
            </DropdownMenuCheckboxItem>

            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[11px] font-normal text-muted-foreground">
              Records spanning several intervals
            </DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={options.spanning}
              onValueChange={(value) =>
                setOption("spanning", value as RecordsViewOptions["spanning"])
              }
            >
              <DropdownMenuRadioItem value="ends" className="text-[13px]">
                Start &amp; end
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="first" className="text-[13px]">
                First only
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="every" className="text-[13px]">
                Every interval
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {inCollection && !notesMode && (
        <div className="flex shrink-0 gap-[3px] rounded-md border border-border bg-card p-[3px]">
          {SCOPE_CHOICES.map((scope) => {
            const active = options.collectionScope === scope;
            return (
              <button
                key={scope}
                type="button"
                title={SCOPE_TITLES[scope]}
                onClick={() => setOption("collectionScope", scope)}
                className={`flex h-[24px] items-center rounded-[5px] px-2 text-[12.5px] transition-colors ${
                  active
                    ? "bg-primary font-semibold text-primary-foreground"
                    : "text-muted-foreground hover:bg-row-hover hover:text-foreground"
                }`}
              >
                {SCOPE_LABELS[scope]}
              </button>
            );
          })}
        </div>
      )}

      {/* The grouping pickers live inline when there's room and fold into the
          display menu when there isn't; order and collapse/expand stay in the
          toolbar at every width. */}
      {!notesMode && inlineControls && (
        <>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className={triggerClass}>
                <span className="text-muted-foreground">Group</span>
                <span className="truncate">{groupLabel}</span>
                <ChevronsUpDown className="size-3.5 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-44">
              {groupByMenu}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className={triggerClass}>
                <span className="text-muted-foreground">Then</span>
                <span className="truncate">{thenLabel}</span>
                <ChevronsUpDown className="size-3.5 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-44">
              {thenByMenu}
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      )}

      {!notesMode && (
        <>
          <Button
            variant="ghost"
            size="icon"
            className={iconButtonClass}
            title={options.order === "asc" ? "Oldest first" : "Newest first"}
            onClick={toggleOrder}
          >
            <OrderIcon className="size-[15px]" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            className={iconButtonClass}
            title="Collapse all"
            onClick={onCollapseAll}
          >
            <FoldVertical className="size-[15px]" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            className={iconButtonClass}
            title="Expand all"
            onClick={onExpandAll}
          >
            <UnfoldVertical className="size-[15px]" />
          </Button>
        </>
      )}

      {notesMode && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className={triggerClass}>
              <span className="text-muted-foreground">Sort</span>
              <span className="truncate">
                {options.notesSort === "created" ? "Created" : "Modified"}
              </span>
              <ChevronsUpDown className="size-3.5 opacity-50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="min-w-44">
            <DropdownMenuRadioGroup
              value={options.notesSort}
              onValueChange={(value) =>
                setOption("notesSort", value as RecordsViewOptions["notesSort"])
              }
            >
              <DropdownMenuRadioItem value="created" className="text-[13px]">
                Created
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="modified" className="text-[13px]">
                Modified
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
            <DropdownMenuSeparator />
            <DropdownMenuRadioGroup
              value={options.notesOrder}
              onValueChange={(value) =>
                setOption(
                  "notesOrder",
                  value as RecordsViewOptions["notesOrder"],
                )
              }
            >
              <DropdownMenuRadioItem value="desc" className="text-[13px]">
                Newest first
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="asc" className="text-[13px]">
                Oldest first
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <div className="flex-1" />

      {/* Widening the range to fit everything in scope. Deliberately dumber
          than the sidebar's press-the-open-collection gesture, which also flips
          to the notes tab once fitted: a button that changed tab would be a
          second control wearing the same icon. */}
      {!notesMode && allRecordsTarget && (
        <Button
          variant="ghost"
          size="icon"
          className={iconButtonClass}
          disabled={fitted}
          title={`${
            fitted
              ? "Already showing every record"
              : "Fit the range to all records"
          } · ${formatFittedRange(allRecordsTarget)}`}
          onClick={() =>
            getGlobalStacks().setIntervals(
              allRecordsTarget.interval,
              allRecordsTarget.endInterval,
            )
          }
        >
          <Scan className="size-[15px]" />
        </Button>
      )}

    </div>
  );
}
